import React, { useState, useEffect } from 'react';
import { useAuth } from '../Auth/AuthProvider';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { 
  listTenants, 
  getTenant, 
  createTenant, 
  updateTenant, 
  deleteTenant 
} from '../../services/platformAdminService';

const {
  FiHome,
  FiUsers,
  FiFileText,
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSearch,
  FiRefreshCw,
  FiX,
  FiAlertTriangle,
  FiCheck,
  FiEye,
  FiBriefcase,
  FiLayers
} = FiIcons;

/**
 * TenantManagement - CRUD interface for managing tenants
 * Includes 2-level confirmation for destructive actions
 */
const TenantManagement = () => {
  const { hasSuperadminPermission } = useAuth();
  
  // State
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [tenantDetails, setTenantDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({ name: '', slug: '', ownerEmail: '' });
  const [formError, setFormError] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  
  // Delete confirmation state
  const [deleteStep, setDeleteStep] = useState(1);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');

  const canWrite = hasSuperadminPermission('tenants:write');

  // Fetch tenants
  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async (search = '') => {
    setLoading(true);
    setError(null);
    try {
      const data = await listTenants({ search });
      setTenants(data.tenants || []);
    } catch (err) {
      setError(err.message || 'Failed to load tenants');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchTenants(searchQuery);
  };

  // View tenant details
  const handleViewDetails = async (tenant) => {
    setSelectedTenant(tenant);
    setShowDetailsModal(true);
    setDetailsLoading(true);
    try {
      const data = await getTenant(tenant.id);
      setTenantDetails(data.tenant);
    } catch (err) {
      setError(err.message);
    } finally {
      setDetailsLoading(false);
    }
  };

  // Create tenant
  const handleCreate = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError(null);
    try {
      await createTenant(formData);
      setShowCreateModal(false);
      setFormData({ name: '', slug: '', ownerEmail: '' });
      fetchTenants();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  // Edit tenant
  const handleEdit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError(null);
    try {
      await updateTenant(selectedTenant.id, formData);
      setShowEditModal(false);
      setSelectedTenant(null);
      setFormData({ name: '', slug: '', ownerEmail: '' });
      fetchTenants();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  // Delete tenant (2-level confirmation)
  const handleDeleteStep1 = () => {
    setDeleteStep(2);
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirmText !== selectedTenant.name) {
      setFormError('Confirmation text does not match tenant name');
      return;
    }
    
    setFormLoading(true);
    setFormError(null);
    try {
      await deleteTenant(selectedTenant.id, deleteConfirmText);
      setShowDeleteModal(false);
      setSelectedTenant(null);
      setDeleteStep(1);
      setDeleteConfirmText('');
      fetchTenants();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setFormLoading(false);
    }
  };

  const openEditModal = (tenant) => {
    setSelectedTenant(tenant);
    setFormData({ name: tenant.name, slug: tenant.slug, ownerEmail: '' });
    setFormError(null);
    setShowEditModal(true);
  };

  const openDeleteModal = (tenant) => {
    setSelectedTenant(tenant);
    setDeleteStep(1);
    setDeleteConfirmText('');
    setFormError(null);
    setShowDeleteModal(true);
  };

  const closeModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setShowDeleteModal(false);
    setShowDetailsModal(false);
    setSelectedTenant(null);
    setTenantDetails(null);
    setFormData({ name: '', slug: '', ownerEmail: '' });
    setFormError(null);
    setDeleteStep(1);
    setDeleteConfirmText('');
  };

  // Auto-generate slug from name
  const handleNameChange = (name, isCreate = false) => {
    setFormData(prev => ({
      ...prev,
      name,
      ...(isCreate ? { slug: name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') } : {})
    }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Tenant Management</h1>
          <p className="text-sm text-gray-500 mt-1">Manage organization accounts</p>
        </div>
        {canWrite && (
          <button
            onClick={() => {
              setFormData({ name: '', slug: '', ownerEmail: '' });
              setFormError(null);
              setShowCreateModal(true);
            }}
            className="flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4" />
            <span>Create Tenant</span>
          </button>
        )}
      </div>

      {/* Search */}
      <form onSubmit={handleSearch} className="flex space-x-2">
        <div className="relative flex-1">
          <SafeIcon icon={FiSearch} className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by name or slug..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </div>
        <button
          type="submit"
          className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          Search
        </button>
        <button
          type="button"
          onClick={() => {
            setSearchQuery('');
            fetchTenants();
          }}
          className="p-2 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
        >
          <SafeIcon icon={FiRefreshCw} className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </form>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Tenants Table */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-2 text-gray-500">Loading tenants...</p>
          </div>
        ) : tenants.length === 0 ? (
          <div className="p-8 text-center">
            <SafeIcon icon={FiHome} className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No tenants found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-4 py-3 text-left">Tenant</th>
                <th className="px-4 py-3 text-center">Users</th>
                <th className="px-4 py-3 text-center">Cases</th>
                <th className="px-4 py-3 text-left">Created</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {tenants.map((tenant) => (
                <tr key={tenant.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-gray-900">{tenant.name}</p>
                      <p className="text-xs text-gray-500">{tenant.slug}</p>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">
                      <SafeIcon icon={FiUsers} className="w-3 h-3 mr-1" />
                      {tenant.user_count}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
                      <SafeIcon icon={FiFileText} className="w-3 h-3 mr-1" />
                      {tenant.case_count}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {new Date(tenant.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleViewDetails(tenant)}
                        className="p-1.5 text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 rounded transition-colors"
                        title="View Details"
                      >
                        <SafeIcon icon={FiEye} className="w-4 h-4" />
                      </button>
                      {canWrite && (
                        <>
                          <button
                            onClick={() => openEditModal(tenant)}
                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                            title="Edit"
                          >
                            <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => openDeleteModal(tenant)}
                            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                            title="Delete"
                          >
                            <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">Create Tenant</h3>
              <button onClick={closeModals} className="p-1 hover:bg-gray-100 rounded">
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleCreate} className="p-6 space-y-4">
              {formError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                  {formError}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleNameChange(e.target.value, true)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="Acme Corporation"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Slug *</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="acme-corporation"
                />
                <p className="text-xs text-gray-500 mt-1">URL-friendly identifier</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Owner Email (optional)</label>
                <input
                  type="email"
                  value={formData.ownerEmail}
                  onChange={(e) => setFormData(prev => ({ ...prev, ownerEmail: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  placeholder="owner@example.com"
                />
                <p className="text-xs text-gray-500 mt-1">Existing user to assign as owner</p>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModals}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formLoading}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                >
                  {formLoading ? 'Creating...' : 'Create Tenant'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedTenant && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">Edit Tenant</h3>
              <button onClick={closeModals} className="p-1 hover:bg-gray-100 rounded">
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleEdit} className="p-6 space-y-4">
              {formError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                  {formError}
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Slug</label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModals}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formLoading}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                >
                  {formLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal (2-level confirmation) */}
      {showDeleteModal && selectedTenant && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-red-200 bg-red-50">
              <div className="flex items-center space-x-2">
                <SafeIcon icon={FiAlertTriangle} className="w-5 h-5 text-red-600" />
                <h3 className="text-lg font-semibold text-red-900">Delete Tenant</h3>
              </div>
              <button onClick={closeModals} className="p-1 hover:bg-red-100 rounded">
                <SafeIcon icon={FiX} className="w-5 h-5 text-red-600" />
              </button>
            </div>
            <div className="p-6">
              {formError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 mb-4">
                  {formError}
                </div>
              )}
              
              {deleteStep === 1 ? (
                <>
                  <p className="text-gray-700 mb-4">
                    Are you sure you want to delete <strong>{selectedTenant.name}</strong>?
                  </p>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-amber-800">
                      <strong>Warning:</strong> This will remove all members from this tenant. 
                      This action cannot be undone.
                    </p>
                  </div>
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={closeModals}
                      className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDeleteStep1}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    >
                      Yes, Continue
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-gray-700 mb-4">
                    To confirm deletion, type the tenant name: <strong>{selectedTenant.name}</strong>
                  </p>
                  <input
                    type="text"
                    value={deleteConfirmText}
                    onChange={(e) => setDeleteConfirmText(e.target.value)}
                    placeholder="Type tenant name to confirm"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 mb-4"
                  />
                  <div className="flex justify-end space-x-3">
                    <button
                      onClick={() => setDeleteStep(1)}
                      className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleDeleteConfirm}
                      disabled={formLoading || deleteConfirmText !== selectedTenant.name}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                    >
                      {formLoading ? 'Deleting...' : 'Confirm Delete'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Details Modal */}
      {showDetailsModal && selectedTenant && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-4 max-h-[80vh] overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">Tenant Details</h3>
              <button onClick={closeModals} className="p-1 hover:bg-gray-100 rounded">
                <SafeIcon icon={FiX} className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-80px)]">
              {detailsLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                </div>
              ) : tenantDetails ? (
                <div className="space-y-6">
                  {/* Basic Info */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Basic Information</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Name</p>
                        <p className="font-medium">{tenantDetails.name}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Slug</p>
                        <p className="font-medium">{tenantDetails.slug}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Created</p>
                        <p className="font-medium">{new Date(tenantDetails.created_at).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">ID</p>
                        <p className="font-mono text-xs">{tenantDetails.id}</p>
                      </div>
                    </div>
                  </div>

                  {/* Stats */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-3">Statistics</h4>
                    <div className="grid grid-cols-4 gap-3">
                      <div className="bg-blue-50 rounded-lg p-3 text-center">
                        <SafeIcon icon={FiUsers} className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                        <p className="text-xl font-bold text-blue-700">{tenantDetails.members?.length || 0}</p>
                        <p className="text-xs text-blue-600">Users</p>
                      </div>
                      <div className="bg-green-50 rounded-lg p-3 text-center">
                        <SafeIcon icon={FiFileText} className="w-5 h-5 text-green-600 mx-auto mb-1" />
                        <p className="text-xl font-bold text-green-700">{tenantDetails.case_count}</p>
                        <p className="text-xs text-green-600">Cases</p>
                      </div>
                      <div className="bg-orange-50 rounded-lg p-3 text-center">
                        <SafeIcon icon={FiBriefcase} className="w-5 h-5 text-orange-600 mx-auto mb-1" />
                        <p className="text-xl font-bold text-orange-700">{tenantDetails.staff_count}</p>
                        <p className="text-xs text-orange-600">Staff</p>
                      </div>
                      <div className="bg-purple-50 rounded-lg p-3 text-center">
                        <SafeIcon icon={FiLayers} className="w-5 h-5 text-purple-600 mx-auto mb-1" />
                        <p className="text-xl font-bold text-purple-700">{tenantDetails.workflow_count}</p>
                        <p className="text-xs text-purple-600">Workflows</p>
                      </div>
                    </div>
                  </div>

                  {/* Members */}
                  {tenantDetails.members && tenantDetails.members.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">Members</h4>
                      <div className="border rounded-lg overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs text-gray-500">Email</th>
                              <th className="px-3 py-2 text-left text-xs text-gray-500">Role</th>
                              <th className="px-3 py-2 text-left text-xs text-gray-500">Joined</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {tenantDetails.members.map((member, idx) => (
                              <tr key={idx}>
                                <td className="px-3 py-2">{member.email}</td>
                                <td className="px-3 py-2">
                                  <span className={`px-2 py-0.5 rounded text-xs ${
                                    member.role === 'owner' ? 'bg-purple-100 text-purple-700' :
                                    member.role === 'admin' ? 'bg-blue-100 text-blue-700' :
                                    'bg-gray-100 text-gray-700'
                                  }`}>
                                    {member.role}
                                  </span>
                                </td>
                                <td className="px-3 py-2 text-gray-500">
                                  {new Date(member.created_at).toLocaleDateString()}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500">Failed to load details</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantManagement;
